import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, RefreshCw, AlertTriangle, ChevronUp, ChevronDown, Download, Eye } from 'lucide-react';
import {
  Bar, BarChart, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, LabelList,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { DashboardLayout } from '../components/DashboardLayout';
import { reportsAPI, brosAPI, invoiceFinancingAPI } from '../lib/api';
import { usePortfolioPrefsStore } from '../store';
import { formatCurrency, cn, deriveInvoiceStatus } from '../lib/utils';
import { formatCompact } from '../lib/chartFormatters';
import {
  pdfCurrencyFormatter, drawBrandedHeader, drawBrandedFooter,
  TABLE_DEFAULTS,
} from '../lib/exportUtils';
import { convertAmount } from '../lib/currencyConvert';
import { CurrencyToggle } from '../components/CurrencyToggle';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/errors';
import { monthKeyToLabel, buildAxisData } from '../lib/forecastChartUtils';
import { BROSOverviewChart } from '../components/bros/BROSOverviewChart';

// \u2500\u2500 Constants \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500

const PERIODS = [
  { value: 'ytd', label: 'YTD' },
  { value: 'last_12m', label: 'Last 12M' },
  { value: 'all', label: 'All Time' },
];

const TABS = [
  { value: 'overview', label: 'Overview' },
  { value: 'bookings-costs', label: 'Bookings vs Supplier Costs' },
  { value: 'bills-payments', label: 'Supplier Bills vs Payments' },
  { value: 'invoice-financing', label: 'Invoice Financing' },
];

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const STATUS_CONFIG = {
  paid: {
    label: 'Paid',
    badge: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 border-0',
    heading: 'text-green-700 dark:text-green-400',
  },
  unpaid: {
    label: 'Unpaid',
    badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 border-0',
    heading: 'text-amber-700 dark:text-amber-400',
  },
  overdue: {
    label: 'Overdue',
    badge: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 border-0',
    heading: 'text-red-700 dark:text-red-400',
  },
  draft: {
    label: 'Draft',
    badge: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400 border-0',
    heading: 'text-gray-700 dark:text-gray-400',
  },
  cancelled: {
    label: 'Cancelled',
    badge: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border-0',
    heading: 'text-red-600 dark:text-red-400',
  },
  void: {
    label: 'Void',
    badge: 'bg-gray-100 text-gray-600 dark:bg-gray-900/30 dark:text-gray-400 border-0',
    heading: 'text-gray-600 dark:text-gray-400',
  },
};

const STATUS_ORDER = ['overdue', 'unpaid', 'paid', 'draft', 'cancelled', 'void'];

// \u2500\u2500 Helpers \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500

function monthKeyToShortLabel(mk) {
  if (!mk || mk.length < 7) return mk;
  const [year, month] = mk.split('-');
  const date = new Date(Number(year), Number(month) - 1, 1);
  return date.toLocaleString('default', { month: 'short', year: '2-digit' });
}

function monthKeyToLongLabel(mk) {
  if (!mk || mk.length < 7) return mk;
  const [year, month] = mk.split('-');
  const date = new Date(Number(year), Number(month) - 1, 1);
  return date.toLocaleString('default', { month: 'long', year: 'numeric' });
}

// \u2500\u2500 Shared pill controls \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500

function PeriodPills(props) {
  const { period, onPeriodChange } = props;
  return (
    <div className="flex gap-0.5 bg-muted rounded-md p-0.5">
      {PERIODS.map((p) => (
        <button
          key={p.value}
          onClick={() => onPeriodChange(p.value)}
          className={cn(
            'px-3 py-1.5 text-sm font-medium rounded transition-all',
            period === p.value
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
}

// \u2500\u2500 Tooltips \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500

function CostsInvoicesTooltip(props) {
  const { active, payload, label, currency } = props;
  if (!active || !payload?.length) return null;
  const costEntry = payload.find((p) => p.dataKey === 'SupplierCost');
  const invoicedEntry = payload.find((p) => p.dataKey === 'SupplierInvoiced');
  const costVal = costEntry?.value;
  const invoicedVal = invoicedEntry?.value;
  const uninvoiced = (costVal ?? 0) - (invoicedVal ?? 0);
  return (
    <div className="rounded-lg border bg-card p-3 shadow-md text-sm">
      <p className="font-medium mb-1">{label}</p>
      {costVal != null && (
        <p style={{ color: '#F87171' }}>Supplier Cost: {formatCurrency(costVal, currency)}</p>
      )}
      {invoicedVal != null && (
        <p style={{ color: '#F59E0B' }}>Supplier Invoiced: {formatCurrency(invoicedVal, currency)}</p>
      )}
      {costVal != null && invoicedVal != null && (
        <p className="text-muted-foreground mt-1 border-t pt-1 text-xs">
          Uninvoiced: {formatCurrency(uninvoiced, currency)}
        </p>
      )}
    </div>
  );
}

function SupplierPaymentsTooltip(props) {
  const { active, payload, label, currency } = props;
  if (!active || !payload?.length) return null;
  const invoicedEntry = payload.find((p) => p.dataKey === 'Invoiced');
  const paidEntry = payload.find((p) => p.dataKey === 'Paid');
  const invoicedVal = invoicedEntry?.value;
  const paidVal = paidEntry?.value;
  const outstanding = (invoicedVal ?? 0) - (paidVal ?? 0);
  return (
    <div className="rounded-lg border bg-card p-3 shadow-md text-sm">
      <p className="font-medium mb-1">{label}</p>
      {invoicedVal != null && (
        <p style={{ color: '#F59E0B' }}>Invoiced: {formatCurrency(invoicedVal, currency)}</p>
      )}
      {paidVal != null && (
        <p style={{ color: '#10B981' }}>Paid: {formatCurrency(paidVal, currency)}</p>
      )}
      {invoicedVal != null && paidVal != null && invoicedVal > 0 && (
        <p className="text-muted-foreground mt-1 border-t pt-1 text-xs">
          Outstanding: {formatCurrency(outstanding, currency)}
        </p>
      )}
    </div>
  );
}

// \u2500\u2500 Shared components \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500

// \u2500\u2500 Data builders \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500

function buildSupplierOverviewChartData(months, invoices, cx) {
  const byMonth = new Map();
  if (months) {
    const sorted = months.slice().sort((a, b) => (a.month ?? '').localeCompare(b.month ?? ''));
    for (const m of sorted) {
      byMonth.set(m.month, {
        monthKey: m.month,
        month: monthKeyToShortLabel(m.month),
        SupplierCost: cx(Number(m.cogs ?? 0)),
        SupplierInvoiced: 0,
      });
    }
  }
  for (const inv of invoices) {
    const bucketDate = inv.due_date || inv.issue_date;
    if (!bucketDate) continue;
    const mk = bucketDate.slice(0, 7);
    if (!byMonth.has(mk)) {
      byMonth.set(mk, {
        monthKey: mk,
        month: monthKeyToShortLabel(mk),
        SupplierCost: 0,
        SupplierInvoiced: 0,
      });
    }
    byMonth.get(mk).SupplierInvoiced += cx(inv.total_amount ?? 0);
  }
  return Array.from(byMonth.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, v]) => v);
}

function buildSupplierBillsChartData(invoices, cx) {
  const byMonth = new Map();
  for (const inv of invoices) {
    const bucketDate = inv.due_date || inv.issue_date;
    if (!bucketDate) continue;
    const mk = bucketDate.slice(0, 7);
    if (!byMonth.has(mk)) {
      byMonth.set(mk, { monthKey: mk, month: monthKeyToShortLabel(mk), Invoiced: 0, Paid: 0 });
    }
    const entry = byMonth.get(mk);
    entry.Invoiced += inv.total_amount ?? 0;
    entry.Paid += inv.amount_paid ?? 0;
  }
  const sorted = Array.from(byMonth.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, v]) => ({ ...v, Invoiced: cx(v.Invoiced), Paid: cx(v.Paid) }));
  for (let i = 0; i < sorted.length; i++) {
    if (i === 0 || !sorted[i - 1].Invoiced) {
      sorted[i].MoMGrowth = null;
    } else {
      sorted[i].MoMGrowth = ((sorted[i].Invoiced - sorted[i - 1].Invoiced) / sorted[i - 1].Invoiced) * 100;
    }
  }
  return sorted;
}

// ── Export functions (mirror Revenue page pattern) ───────────────────

async function exportCostsExcel({ activeTab, dc, revCostsChartData, billsChartData, monthlyRows, invoices, cxInv }) {
  const XLSX = await import('xlsx');
  const wb = XLSX.utils.book_new();
  const fmtV = (v) => v != null ? Math.round(v * 100) / 100 : '';
  const curSuffix = dc === 'EUR' ? '-eur' : '';

  if (activeTab === 'overview') {
    const rcRows = (revCostsChartData || []).map(d => ({
      Month: d.month,
      'Supplier Cost': fmtV(d.SupplierCost),
      'Supplier Invoiced': fmtV(d.SupplierInvoiced),
      Uninvoiced: fmtV((d.SupplierCost ?? 0) - (d.SupplierInvoiced ?? 0)),
    }));
    if (rcRows.length) {
      const ws1 = XLSX.utils.json_to_sheet(rcRows);
      XLSX.utils.book_append_sheet(wb, ws1, 'Supplier Costs vs Invoices');
    }
    const billRows = (billsChartData || []).map(d => ({
      Month: d.month,
      Invoiced: fmtV(d.Invoiced),
      Paid: fmtV(d.Paid),
    }));
    if (billRows.length) {
      const ws2 = XLSX.utils.json_to_sheet(billRows);
      XLSX.utils.book_append_sheet(wb, ws2, 'Invoices vs Payments');
    }
  } else if (activeTab === 'bookings-costs') {
    const rows = (monthlyRows || []).map(r => ({
      Month: r.monthLabel || r.monthShort,
      Revenue: fmtV(r.revenue),
      'Supplier Cost': fmtV(r.cogs),
      'Gross Margin': fmtV(r.margin),
      'Margin %': r.marginPct != null ? `${r.marginPct.toFixed(1)}%` : '',
      Bookings: r.bookingCount,
    }));
    if (rows.length) {
      const ws = XLSX.utils.json_to_sheet(rows);
      XLSX.utils.book_append_sheet(wb, ws, 'Bookings vs Supplier Costs');
    }
  } else {
    const rows = (invoices || []).map(inv => ({
      Supplier: inv.supplier_name || inv.company || '',
      Location: inv.property_name || '',
      'Booking ID': inv.booking_id || '',
      Invoice: inv.invoice_number || inv.bros_id || '',
      'Issue Date': inv.issue_date || '',
      'Due Date': inv.due_date || '',
      'Native Currency': inv.currency || 'GBP',
      Amount: fmtV(cxInv ? cxInv(inv.total_amount, inv.currency) : inv.total_amount),
      Paid: fmtV(cxInv ? cxInv(inv.amount_paid, inv.currency) : inv.amount_paid),
      Outstanding: fmtV(cxInv ? cxInv(inv.total_amount, inv.currency) - cxInv(inv.amount_paid, inv.currency) : (inv.total_amount ?? 0) - (inv.amount_paid ?? 0)),
      Status: deriveInvoiceStatus(inv),
      'Display Currency': dc,
    }));
    if (rows.length) {
      const ws = XLSX.utils.json_to_sheet(rows);
      XLSX.utils.book_append_sheet(wb, ws, 'Supplier Bills');
    }
  }

  if (wb.SheetNames.length === 0) {
    const ws = XLSX.utils.json_to_sheet([{ Note: 'No data to export' }]);
    XLSX.utils.book_append_sheet(wb, ws, 'Empty');
  }

  const fileName = `bros-costs-${activeTab}${curSuffix}.xlsx`;
  XLSX.writeFile(wb, fileName);
}

async function exportCostsPDF({ activeTab, dc, revCostsChartData, billsChartData, monthlyRows, invoices, cxInv }) {
  const { default: jsPDF } = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const fmtV = pdfCurrencyFormatter(dc);
  const curSuffix = dc === 'EUR' ? '-eur' : '';
  const tabLabel = activeTab === 'overview' ? 'Overview'
    : activeTab === 'bookings-costs' ? 'Bookings vs Supplier Costs'
    : 'Supplier Bills vs Payments';

  let y = drawBrandedHeader(doc, {
    title: 'Bonjour Residences',
    scopeLine: `Costs: ${tabLabel} (${dc})`,
  });

  if (activeTab === 'overview') {
    const rcRows = (revCostsChartData || []).map(d => [
      d.month, fmtV(d.SupplierCost), fmtV(d.SupplierInvoiced),
      fmtV((d.SupplierCost ?? 0) - (d.SupplierInvoiced ?? 0)),
    ]);
    if (rcRows.length) {
      autoTable(doc, {
        ...TABLE_DEFAULTS,
        startY: y,
        head: [['Month', 'Supplier Cost', 'Supplier Invoiced', 'Uninvoiced']],
        body: rcRows,
      });
      y = doc.lastAutoTable.finalY + 8;
    }
    const billRows = (billsChartData || []).map(d => [d.month, fmtV(d.Invoiced), fmtV(d.Paid)]);
    if (billRows.length) {
      autoTable(doc, {
        ...TABLE_DEFAULTS,
        startY: y,
        head: [['Month', 'Invoiced', 'Paid']],
        body: billRows,
      });
    }
  } else if (activeTab === 'bookings-costs') {
    const rows = (monthlyRows || []).map(r => [
      r.monthLabel || r.monthShort,
      fmtV(r.revenue),
      fmtV(r.cogs),
      fmtV(r.margin),
      r.marginPct != null ? `${r.marginPct.toFixed(1)}%` : '',
      r.bookingCount,
    ]);
    if (rows.length) {
      autoTable(doc, {
        ...TABLE_DEFAULTS,
        startY: y,
        head: [['Month', 'Revenue', 'Supplier Cost', 'Margin', 'Margin %', 'Bookings']],
        body: rows,
      });
    }
  } else {
    const rows = (invoices || []).map(inv => [
      inv.supplier_name || inv.company || '',
      inv.property_name || '',
      inv.booking_id || '',
      inv.invoice_number || inv.bros_id || '',
      inv.due_date || '',
      inv.currency || 'GBP',
      fmtV(cxInv ? cxInv(inv.total_amount, inv.currency) : inv.total_amount),
      deriveInvoiceStatus(inv),
    ]);
    if (rows.length) {
      autoTable(doc, {
        ...TABLE_DEFAULTS,
        startY: y,
        head: [['Supplier', 'Location', 'Booking', 'Invoice', 'Due Date', 'Ccy', 'Amount', 'Status']],
        body: rows,
      });
    }
  }

  drawBrandedFooter(doc);

  const fileName = `bros-costs-${activeTab}${curSuffix}.pdf`;
  doc.save(fileName);
}

// \u2500\u2500 Overview Tab \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500

function MoMGrowthRow(props) {
  const { data, label } = props;
  if (!data || data.length === 0) return null;
  return (
    <div className="mt-2 border-t pt-1.5">
      <div className="flex items-center">
        <span className="text-[10px] text-muted-foreground shrink-0 w-14 text-right pr-2">{label ?? 'MoM'}</span>
        <div className="flex flex-1 min-w-0">
          {data.map((d, i) => (
            <div key={d.monthKey ?? i} className="flex-1 text-center text-[10px] font-mono">
              {d.MoMGrowth == null ? (
                <span className="text-muted-foreground/50">-</span>
              ) : (
                <span className={d.MoMGrowth >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                  {d.MoMGrowth >= 0 ? '+' : ''}{Math.round(d.MoMGrowth)}%
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function OverviewTab(props) {
  // Props destructured in body (babel-metadata-plugin workaround)
  const { period, dc, cx, dashData, dashLoading, supplierInvoices, invoicesLoading, cxInv } = props;
  // Supplier invoices are in their OWN currency (DKK/EUR/GBP/...) and the API
  // sends amounts as strings: `cx` (a GBP->display toggle) concatenated
  // "0" + "2368.0" and never converted DKK, so the chart said "No supplier
  // invoices found" while 112 existed (2026-10-06).
  const invAmount = (inv, field) => (cxInv ? cxInv(Number(inv[field] ?? 0), inv.currency) : Number(inv[field] ?? 0));

  // Chart 1: Supplier Costs (from bookings) vs Supplier Invoices (from bros_invoices)
  const supplierOverviewData = useMemo(() => {
    const monthMap = {};
    if (dashData?.months) {
      for (const m of dashData.months) {
        monthMap[m.month] = {
          SupplierCost: cx(Number(m.cogs ?? 0)),
          SupplierInvoiced: 0,
        };
      }
    }
    for (const inv of supplierInvoices) {
      const bucketDate = inv.due_date || inv.issue_date;
      if (!bucketDate) continue;
      const mk = bucketDate.slice(0, 7);
      if (!monthMap[mk]) monthMap[mk] = { SupplierCost: 0, SupplierInvoiced: 0 };
      monthMap[mk].SupplierInvoiced += invAmount(inv, 'total_amount');
    }
    return buildAxisData(period, monthMap, ['SupplierCost', 'SupplierInvoiced']);
  }, [dashData, supplierInvoices, cx, period, cxInv]);

  // Chart 2: Supplier Invoices vs Supplier Payments (from bros_invoices)
  const supplierPaymentsData = useMemo(() => {
    const monthMap = {};
    for (const inv of supplierInvoices) {
      const bucketDate = inv.due_date || inv.issue_date;
      if (!bucketDate) continue;
      const mk = bucketDate.slice(0, 7);
      if (!monthMap[mk]) monthMap[mk] = { Invoiced: 0, Paid: 0 };
      monthMap[mk].Invoiced += invAmount(inv, 'total_amount');
      monthMap[mk].Paid += invAmount(inv, 'amount_paid');
    }
    return buildAxisData(period, monthMap, ['Invoiced', 'Paid']);
  }, [supplierInvoices, cx, period, cxInv]);

  // Diagnostic checks
  const costsHasData = supplierOverviewData.some((d) => d.SupplierCost > 0);
  const invoicedHasData = supplierOverviewData.some((d) => d.SupplierInvoiced > 0);
  const billsHasData = supplierPaymentsData.some((d) => d.Invoiced > 0);
  const paymentsHasData = supplierPaymentsData.some((d) => d.Paid > 0);

  // Totals for Supplier Costs vs Invoices footer
  const costInvTotals = useMemo(() => {
    let totalCost = 0;
    let totalInvoiced = 0;
    for (const d of supplierOverviewData) {
      totalCost += d.SupplierCost ?? 0;
      totalInvoiced += d.SupplierInvoiced ?? 0;
    }
    return { cost: totalCost, invoiced: totalInvoiced, uninvoiced: totalCost - totalInvoiced };
  }, [supplierOverviewData]);

  // Totals for Supplier Invoices vs Payments footer
  const paymentTotals = useMemo(() => {
    let totalInvoiced = 0;
    let totalPaid = 0;
    for (const d of supplierPaymentsData) {
      totalInvoiced += d.Invoiced ?? 0;
      totalPaid += d.Paid ?? 0;
    }
    return { invoiced: totalInvoiced, paid: totalPaid, outstanding: totalInvoiced - totalPaid };
  }, [supplierPaymentsData]);

  // Diagnostic notes
  const costInvDiagnosticNote = (costsHasData && !invoicedHasData)
    ? 'Supplier cost data present (booking supplier_total). No supplier invoices found in bros_invoices.'
    : (!costsHasData && invoicedHasData)
    ? 'Supplier invoices present. Supplier cost (booking supplier_total) reads zero for all months.'
    : null;

  const paymentsDiagnosticNote = (billsHasData && !paymentsHasData)
    ? 'Supplier invoices present but no payments recorded (amount_paid reads zero on all invoices).'
    : null;

  return (
    <div className="grid grid-cols-1 gap-6">
      {/* Chart 1: Supplier Costs vs Supplier Invoices (via BROSOverviewChart) */}
      <BROSOverviewChart
        data={supplierOverviewData}
        series={[
          { dataKey: 'SupplierCost', name: 'Supplier Cost', color: '#F87171' },
          { dataKey: 'SupplierInvoiced', name: 'Supplier Invoiced', color: '#F59E0B' },
        ]}
        title="Supplier Costs vs Supplier Invoices"
        caption="Supplier costs by booking month. Supplier invoiced by due date (issue date fallback)."
        dc={dc}
        loading={(dashLoading && !dashData) || (invoicesLoading && supplierInvoices.length === 0)}
        emptyMessage="No supplier cost or invoice data for this period. Feeds: booking supplier_total via /reports/bros/dashboard, bros_invoices (type=supplier) via /bros/invoices."
        tooltipContent={<CostsInvoicesTooltip currency={dc} />}
        footerLabel="YTD Supplier Costs vs Invoices"
        footerItems={[
          { label: 'Supplier Cost', value: costInvTotals.cost, color: '#F87171' },
          { label: 'Supplier Invoiced', value: costInvTotals.invoiced, color: '#F59E0B' },
          { label: 'Uninvoiced', value: costInvTotals.uninvoiced, color: '#000000',
            className: costInvTotals.uninvoiced > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-foreground' },
        ]}
        notes={costInvDiagnosticNote && (
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">{costInvDiagnosticNote}</p>
        )}
        growthDataKey="SupplierCost"
      />

      {/* Chart 2: Supplier Invoices vs Supplier Payments (via BROSOverviewChart) */}
      <BROSOverviewChart
        data={supplierPaymentsData}
        series={[
          { dataKey: 'Invoiced', name: 'Invoiced', color: '#F59E0B' },
          { dataKey: 'Paid', name: 'Paid', color: '#10B981' },
        ]}
        title="Supplier Invoices vs Supplier Payments"
        caption="Supplier invoiced and paid by due date (issue date fallback)."
        dc={dc}
        loading={invoicesLoading && supplierInvoices.length === 0}
        emptyMessage="No supplier invoice data available. Feed: bros_invoices (type=supplier) from /bros/invoices."
        tooltipContent={<SupplierPaymentsTooltip currency={dc} />}
        footerLabel="YTD Supplier Invoices vs Payments"
        footerItems={[
          { label: 'Invoiced', value: paymentTotals.invoiced, color: '#F59E0B' },
          { label: 'Paid', value: paymentTotals.paid, color: '#10B981' },
          { label: 'Outstanding', value: paymentTotals.outstanding, color: '#000000',
            className: paymentTotals.outstanding > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-foreground' },
        ]}
        notes={paymentsDiagnosticNote && (
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">{paymentsDiagnosticNote}</p>
        )}
        growthDataKey="Invoiced"
      />
    </div>
  );
}

// \u2500\u2500 Bookings vs Supplier Costs Tab \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500

function BookingsCostsTab(props) {
  const { dc, cx, dashData, dashLoading } = props;
  const [expandedMonths, setExpandedMonths] = useState(new Set());

  const kpis = dashData?.kpis ?? {};
  const totalRevenue = cx(Number(kpis.total_turnover ?? 0));
  const totalCogs = cx(Number(kpis.total_cogs ?? 0));
  const totalMargin = cx(Number(kpis.total_gross_margin ?? 0));
  const marginPct = kpis.margin_pct ?? 0;
  const bookingsCount = kpis.total_bookings ?? 0;

  const monthlyRows = useMemo(() => {
    if (!dashData?.months) return [];
    return dashData.months
      .slice()
      .sort((a, b) => (a.month ?? '').localeCompare(b.month ?? ''))
      .map((m) => {
        const rev = Number(m.turnover ?? 0);
        const cogs = Number(m.cogs ?? 0);
        const margin = Number(m.gross_margin ?? 0);
        const rowMarginPct = rev > 0 ? (margin / rev) * 100 : null;
        return {
          monthKey: m.month,
          monthLabel: monthKeyToLongLabel(m.month),
          monthShort: monthKeyToShortLabel(m.month),
          revenue: cx(rev),
          cogs: cx(cogs),
          margin: cx(margin),
          marginPct: rowMarginPct,
          bookingCount: m.booking_count ?? 0,
        };
      });
  }, [dashData, cx]);

  // Totals across all months
  const totals = useMemo(() => {
    let tRevenue = 0;
    let tCogs = 0;
    let tMargin = 0;
    let tBookings = 0;
    for (const r of monthlyRows) {
      tRevenue += r.revenue;
      tCogs += r.cogs;
      tMargin += r.margin;
      tBookings += r.bookingCount;
    }
    return {
      revenue: tRevenue,
      cogs: tCogs,
      margin: tMargin,
      marginPct: tRevenue > 0 ? (tMargin / tRevenue) * 100 : null,
      bookings: tBookings,
    };
  }, [monthlyRows]);

  const toggleMonth = useCallback((mk) => {
    setExpandedMonths((prev) => {
      const next = new Set(prev);
      next.has(mk) ? next.delete(mk) : next.add(mk);
      return next;
    });
  }, []);

  if (dashLoading && !dashData) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="bookings-costs-tab">
      {/* Summary cards (PP pattern) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Total Supplier Cost</p>
            <p className="text-xl font-bold font-mono mt-1">
              {formatCurrency(totalCogs, dc)}
            </p>
            <p className="text-xs text-muted-foreground">
              {bookingsCount} booking{bookingsCount !== 1 ? 's' : ''}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Total Revenue</p>
            <p className="text-xl font-bold font-mono mt-1 text-blue-600 dark:text-blue-400">
              {formatCurrency(totalRevenue, dc)}
            </p>
            <p className="text-xs text-muted-foreground">Context only</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Gross Margin</p>
            <p className={cn(
              'text-xl font-bold font-mono mt-1',
              totalMargin >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
            )}>
              {formatCurrency(totalMargin, dc)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Margin %</p>
            <p className={cn(
              'text-xl font-bold font-mono mt-1',
              marginPct >= 30 ? 'text-green-600 dark:text-green-400'
                : marginPct >= 15 ? 'text-amber-600 dark:text-amber-400'
                : 'text-red-600 dark:text-red-400'
            )}>
              {marginPct != null ? `${Number(marginPct).toFixed(1)}%` : '-'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Monthly expandable table (PP Bookings-tab structure) */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Month</TableHead>
                  <TableHead className="text-right">Revenue</TableHead>
                  <TableHead className="text-right">Supplier Cost</TableHead>
                  <TableHead className="text-right">Margin</TableHead>
                  <TableHead className="text-right">Margin %</TableHead>
                  <TableHead className="text-right">Bookings</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {monthlyRows.map((row) => {
                  const hasData = row.revenue > 0 || row.cogs > 0;
                  const isExpanded = expandedMonths.has(row.monthKey);
                  const marginColor = row.marginPct == null ? 'text-muted-foreground'
                    : row.marginPct >= 30 ? 'text-green-600 dark:text-green-400'
                    : row.marginPct >= 15 ? 'text-amber-600 dark:text-amber-400'
                    : 'text-red-600 dark:text-red-400';

                  return (
                    <React.Fragment key={row.monthKey}>
                      <TableRow
                        className={cn(hasData && 'cursor-pointer hover:bg-muted/50')}
                        onClick={hasData ? () => toggleMonth(row.monthKey) : undefined}
                      >
                        <TableCell className="w-8 px-2">
                          {hasData && (isExpanded
                            ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
                            : <ChevronDown className="h-4 w-4 text-muted-foreground" />
                          )}
                        </TableCell>
                        <TableCell className="font-medium text-sm">
                          {row.monthLabel}
                          {row.bookingCount > 0 && (
                            <span className="ml-2 text-xs text-muted-foreground">
                              {row.bookingCount} booking{row.bookingCount !== 1 ? 's' : ''}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm text-blue-600 dark:text-blue-400">
                          {hasData ? formatCurrency(row.revenue, dc) : '-'}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {hasData ? formatCurrency(row.cogs, dc) : '-'}
                        </TableCell>
                        <TableCell className={cn(
                          'text-right font-mono text-sm',
                          row.margin >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                        )}>
                          {hasData ? formatCurrency(row.margin, dc) : '-'}
                        </TableCell>
                        <TableCell className={cn('text-right font-mono text-sm', marginColor)}>
                          {row.marginPct != null ? `${row.marginPct.toFixed(1)}%` : '-'}
                        </TableCell>
                        <TableCell className="text-right text-sm text-muted-foreground">
                          {row.bookingCount > 0 ? row.bookingCount : '-'}
                        </TableCell>
                      </TableRow>

                      {/* Expanded detail (PP pattern) */}
                      {isExpanded && (
                        <TableRow className="bg-muted/20">
                          <TableCell></TableCell>
                          <TableCell colSpan={6} className="py-3">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                              <div className="border rounded-lg p-3 bg-card">
                                <p className="text-xs text-muted-foreground">Revenue</p>
                                <p className="text-base font-semibold font-mono text-blue-600 dark:text-blue-400">
                                  {formatCurrency(row.revenue, dc)}
                                </p>
                              </div>
                              <div className="border rounded-lg p-3 bg-card">
                                <p className="text-xs text-muted-foreground">Supplier Cost (COGS)</p>
                                <p className="text-base font-semibold font-mono">
                                  {formatCurrency(row.cogs, dc)}
                                </p>
                              </div>
                              <div className="border rounded-lg p-3 bg-card">
                                <p className="text-xs text-muted-foreground">Gross Margin</p>
                                <p className={cn(
                                  'text-base font-semibold font-mono',
                                  row.margin >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                                )}>
                                  {formatCurrency(row.margin, dc)}
                                </p>
                              </div>
                              <div className="border rounded-lg p-3 bg-card">
                                <p className="text-xs text-muted-foreground">Margin %</p>
                                <p className={cn('text-base font-semibold font-mono', marginColor)}>
                                  {row.marginPct != null ? `${row.marginPct.toFixed(1)}%` : '-'}
                                </p>
                                {row.marginPct != null && row.marginPct > 80 && (
                                  <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-1">
                                    High margin, review supplier costs
                                  </p>
                                )}
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}

                {/* Total row */}
                {monthlyRows.length > 0 && (
                  <TableRow className="font-semibold border-t-2">
                    <TableCell></TableCell>
                    <TableCell className="text-sm">Total</TableCell>
                    <TableCell className="text-right font-mono text-sm text-blue-600 dark:text-blue-400">
                      {formatCurrency(totals.revenue, dc)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {formatCurrency(totals.cogs, dc)}
                    </TableCell>
                    <TableCell className={cn(
                      'text-right font-mono text-sm',
                      totals.margin >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                    )}>
                      {formatCurrency(totals.margin, dc)}
                    </TableCell>
                    <TableCell className={cn(
                      'text-right font-mono text-sm',
                      totals.marginPct != null && totals.marginPct >= 30 ? 'text-green-600 dark:text-green-400'
                        : totals.marginPct != null && totals.marginPct >= 15 ? 'text-amber-600 dark:text-amber-400'
                        : 'text-red-600 dark:text-red-400'
                    )}>
                      {totals.marginPct != null ? `${totals.marginPct.toFixed(1)}%` : '-'}
                    </TableCell>
                    <TableCell className="text-right text-sm text-muted-foreground">
                      {totals.bookings}
                    </TableCell>
                  </TableRow>
                )}

                {monthlyRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No monthly data for this period.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// \u2500\u2500 Supplier Bills vs Payments Tab \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500

function BillsPaymentsTab(props) {
  const { dc, fxRates, invoices, loading, onRefresh, exportScopeRef } = props;
  const [viewMode, setViewMode] = useState('by-supplier');
  const [expandedMonths, setExpandedMonths] = useState(new Set());
  const [expandedSuppliers, setExpandedSuppliers] = useState(new Set());

  // Convert amount from invoice native currency to display currency.
  // Pass full fxRates map (not just EUR_GBP scalar) so DKK, USD, etc. convert correctly.
  const cxInv = useCallback((amount, invCurrency) => {
    if (amount == null) return 0;
    return convertAmount(amount, invCurrency || 'GBP', dc, fxRates) || 0;
  }, [dc, fxRates]);

  // Status-grouped KPIs (currency-aware, derived overdue)
  const byStatus = useMemo(() => {
    const groups = {};
    for (const inv of invoices) {
      const s = deriveInvoiceStatus(inv);
      if (!groups[s]) groups[s] = [];
      groups[s].push(inv);
    }
    return groups;
  }, [invoices]);

  const kpis = useMemo(() => {
    return STATUS_ORDER
      .filter((s) => byStatus[s] && byStatus[s].length > 0)
      .map((s) => ({
        status: s,
        count: byStatus[s].length,
        total: byStatus[s].reduce((sum, inv) => sum + cxInv(inv.total_amount, inv.currency), 0),
      }));
  }, [byStatus, cxInv]);

  // Stat card totals (outstanding derived as billed minus paid, not from
  // amount_outstanding which the backend flags as unreliable for queries)
  const cardTotals = useMemo(() => {
    let totalBilled = 0;
    let totalPaid = 0;
    let overdueAmount = 0;
    let overdueCount = 0;
    for (const inv of invoices) {
      const amt = cxInv(inv.total_amount, inv.currency);
      const paid = cxInv(inv.amount_paid, inv.currency);
      totalBilled += amt;
      totalPaid += paid;
      if (deriveInvoiceStatus(inv) === 'overdue') {
        overdueAmount += amt - paid;
        overdueCount++;
      }
    }
    const totalOutstanding = totalBilled - totalPaid;
    const overduePct = totalOutstanding > 0 ? (overdueAmount / totalOutstanding) * 100 : null;
    return { totalBilled, totalOutstanding, overdueAmount, overdueCount, overduePct };
  }, [invoices, cxInv]);

  // Group by supplier, overdue suppliers first (uses derived status), with aggregates.
  // Prefer enriched supplier_name (resolved from property/booking) over raw company.
  const supplierGroups = useMemo(() => {
    const map = new Map();
    for (const inv of invoices) {
      const supplier = inv.supplier_name || inv.company || '(unknown)';
      if (!map.has(supplier)) {
        map.set(supplier, { supplier, invoices: [], hasOverdue: false, invoiced: 0, paid: 0, overdueCount: 0, overdueAmount: 0 });
      }
      const group = map.get(supplier);
      group.invoices.push(inv);
      const invAmount = cxInv(inv.total_amount, inv.currency);
      const paidAmount = cxInv(inv.amount_paid, inv.currency);
      group.invoiced += invAmount;
      group.paid += paidAmount;
      if (deriveInvoiceStatus(inv) === 'overdue') {
        group.hasOverdue = true;
        group.overdueCount++;
        group.overdueAmount += invAmount - paidAmount;
      }
    }
    const groups = Array.from(map.values());
    for (const g of groups) g.outstanding = g.invoiced - g.paid;
    groups.sort((a, b) => {
      if (a.hasOverdue && !b.hasOverdue) return -1;
      if (!a.hasOverdue && b.hasOverdue) return 1;
      return a.supplier.localeCompare(b.supplier);
    });
    for (const g of groups) {
      g.invoices.sort((a, b) => {
        const aOverdue = deriveInvoiceStatus(a) === 'overdue' ? 0 : 1;
        const bOverdue = deriveInvoiceStatus(b) === 'overdue' ? 0 : 1;
        if (aOverdue !== bOverdue) return aOverdue - bOverdue;
        return (a.due_date || '').localeCompare(b.due_date || '');
      });
    }
    return groups;
  }, [invoices, cxInv]);

  const overdueCount = useMemo(() => {
    let n = 0;
    for (const inv of invoices) if (deriveInvoiceStatus(inv) === 'overdue') n++;
    return n;
  }, [invoices]);

  const overdueTotal = useMemo(() => {
    let sum = 0;
    for (const inv of invoices) {
      if (deriveInvoiceStatus(inv) === 'overdue') sum += cxInv(inv.total_amount, inv.currency);
    }
    return sum;
  }, [invoices, cxInv]);

  // Monthly groups for By Month view
  const monthlyGroups = useMemo(() => {
    const byMonth = new Map();
    for (const inv of invoices) {
      const bucketDate = inv.due_date || inv.issue_date;
      if (!bucketDate) continue;
      const mk = bucketDate.slice(0, 7);
      if (!byMonth.has(mk)) {
        byMonth.set(mk, { monthKey: mk, invoices: [], invoiced: 0, paid: 0, overdueCount: 0, overdueAmount: 0 });
      }
      const group = byMonth.get(mk);
      group.invoices.push(inv);
      const amt = cxInv(inv.total_amount, inv.currency);
      const paid = cxInv(inv.amount_paid, inv.currency);
      group.invoiced += amt;
      group.paid += paid;
      if (deriveInvoiceStatus(inv) === 'overdue') {
        group.overdueCount++;
        group.overdueAmount += amt - paid;
      }
    }
    return Array.from(byMonth.values()).sort((a, b) => b.monthKey.localeCompare(a.monthKey));
  }, [invoices, cxInv]);

  const toggleMonth = useCallback((mk) => {
    setExpandedMonths((prev) => {
      const next = new Set(prev);
      next.has(mk) ? next.delete(mk) : next.add(mk);
      return next;
    });
  }, []);

  const toggleSupplier = useCallback((supplierName) => {
    setExpandedSuppliers((prev) => {
      const next = new Set(prev);
      next.has(supplierName) ? next.delete(supplierName) : next.add(supplierName);
      return next;
    });
  }, []);

  // Keep export scope ref in sync for scoped exports
  useEffect(() => {
    if (exportScopeRef) {
      exportScopeRef.current = { viewMode, expandedSuppliers, supplierGroups };
    }
  }, [exportScopeRef, viewMode, expandedSuppliers, supplierGroups]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="bills-payments-tab">
      {/* KPI stat cards (currency-aware) */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4" data-testid="bills-kpi-row">
        {/* Total Billed: amount headline, count subline */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <Badge className="text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 border-0">Total Billed</Badge>
            </div>
            <div className="text-xl font-bold font-mono mt-2" data-testid="total-billed-amount">
              {formatCurrency(cardTotals.totalBilled, dc)}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">{invoices.length} bill{invoices.length !== 1 ? 's' : ''}</p>
          </CardContent>
        </Card>
        {/* Total Outstanding (what we owe) */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <Badge className="text-xs bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 border-0">Total Outstanding</Badge>
            </div>
            <div className={cn('text-xl font-bold font-mono mt-2', cardTotals.totalOutstanding > 0 ? 'text-amber-700 dark:text-amber-400' : '')}>
              {formatCurrency(cardTotals.totalOutstanding, dc)}
            </div>
          </CardContent>
        </Card>
        {kpis.map((k) => (
          <Card key={k.status}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <Badge className={cn('text-xs', (STATUS_CONFIG[k.status] || {}).badge)}>{(STATUS_CONFIG[k.status] || {}).label || k.status}</Badge>
                <span className="text-xs text-muted-foreground">{k.count} bill{k.count !== 1 ? 's' : ''}</span>
              </div>
              <div className={cn('text-xl font-bold font-mono mt-2', (STATUS_CONFIG[k.status] || {}).heading)}>
                {formatCurrency(k.total, dc)}
              </div>
              {k.status === 'overdue' && cardTotals.overduePct != null && (
                <p className="text-xs text-muted-foreground mt-0.5" data-testid="overdue-pct-of-outstanding">
                  {Math.round(cardTotals.overduePct)}% of outstanding
                </p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Overdue alert */}
      {overdueCount > 0 && (
        <div className="flex items-center gap-2 text-sm text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-md px-4 py-3">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {overdueCount} overdue supplier bill{overdueCount !== 1 ? 's' : ''} totalling {formatCurrency(overdueTotal, dc)}
        </div>
      )}

      {/* Supplier bills table with By Supplier / By Month toggle */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Supplier Bills</CardTitle>
            <div className="flex items-center gap-2">
              <div className="flex gap-0.5 bg-muted rounded-md p-0.5" data-testid="bills-view-toggle">
                {[{ value: 'by-supplier', label: 'By Supplier' }, { value: 'by-month', label: 'By Month' }].map((v) => (
                  <button
                    key={v.value}
                    onClick={() => setViewMode(v.value)}
                    className={cn(
                      'px-2.5 py-1 text-xs font-medium rounded transition-all',
                      viewMode === v.value
                        ? 'bg-background text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    )}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading}>
                <RefreshCw className={cn('h-4 w-4 mr-2', loading && 'animate-spin')} />
                Refresh
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            {invoices.length} bill{invoices.length !== 1 ? 's' : ''} across {supplierGroups.length} supplier{supplierGroups.length !== 1 ? 's' : ''}
            {dc !== 'GBP' && ' (amounts converted to ' + dc + ')'}
          </p>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            {viewMode === 'by-supplier' && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead className="text-right">Billed</TableHead>
                  <TableHead className="text-right">Paid</TableHead>
                  <TableHead className="text-right">Outstanding</TableHead>
                  <TableHead className="text-right">Overdue</TableHead>
                  <TableHead className="text-right">Overdue Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {supplierGroups.map((group) => {
                  const isExpanded = expandedSuppliers.has(group.supplier);
                  return (
                  <React.Fragment key={group.supplier}>
                    {/* Supplier aggregate row (expandable) */}
                    <TableRow
                      className={cn(
                        'cursor-pointer hover:bg-muted/50',
                        group.hasOverdue && 'border-l-2 border-l-red-500'
                      )}
                      onClick={() => toggleSupplier(group.supplier)}
                      data-testid={`supplier-row-${group.supplier}`}
                    >
                      <TableCell className="w-8 px-2">
                        {isExpanded
                          ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
                          : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                      </TableCell>
                      <TableCell className="font-medium text-sm">
                        {group.supplier}
                        <span className="ml-2 text-xs text-muted-foreground">
                          {group.invoices.length} bill{group.invoices.length !== 1 ? 's' : ''}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {formatCurrency(group.invoiced, dc)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm text-green-600 dark:text-green-400">
                        {formatCurrency(group.paid, dc)}
                      </TableCell>
                      <TableCell className={cn(
                        'text-right font-mono text-sm',
                        group.outstanding > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-foreground'
                      )}>
                        {formatCurrency(group.outstanding, dc)}
                      </TableCell>
                      <TableCell className="text-right text-sm">
                        {group.overdueCount > 0 ? (
                          <Badge className="text-[10px] px-1.5 py-0 bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 border-0">
                            {group.overdueCount}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">{'\u2014'}</span>
                        )}
                      </TableCell>
                      <TableCell className={cn(
                        'text-right font-mono text-sm',
                        group.overdueAmount > 0 ? 'text-red-600 dark:text-red-400' : 'text-foreground'
                      )}>
                        {group.overdueAmount > 0 ? formatCurrency(group.overdueAmount, dc) : '\u2014'}
                      </TableCell>
                    </TableRow>
                    {/* Expanded: bill detail rows */}
                    {isExpanded && group.invoices.map((inv, i) => {
                      const cfg = STATUS_CONFIG[deriveInvoiceStatus(inv)] || {};
                      const hasPdf = !!inv.file_url;
                      return (
                        <TableRow key={inv.bros_id || `${group.supplier}-${i}`} className="bg-muted/20 text-xs">
                          <TableCell></TableCell>
                          <TableCell className="py-1.5" colSpan={2}>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
                              {/* Supplier name (from property/booking) */}
                              <span className="font-medium">{inv.supplier_name || inv.company || '-'}</span>
                              {/* Location (property name) */}
                              {inv.property_name && (
                                <span className="text-muted-foreground">{inv.property_name}</span>
                              )}
                              {/* Booking ID linked to BR OS */}
                              {inv.booking_id && (
                                <a
                                  href={`https://internal.bonjour-residences.co.uk/bookings/${inv.booking_id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="font-mono text-blue-600 dark:text-blue-400 hover:underline"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {inv.booking_id}
                                </a>
                              )}
                              {/* Invoice number */}
                              {inv.invoice_number && (
                                <span className="font-mono text-muted-foreground">{inv.invoice_number}</span>
                              )}
                              {/* Amount with native currency */}
                              <span className="font-mono">
                                {inv.currency && inv.currency !== dc && (
                                  <Badge variant="outline" className="text-[10px] px-1 py-0 mr-1">{inv.currency}</Badge>
                                )}
                                {formatCurrency(cxInv(inv.total_amount, inv.currency), dc)}
                              </span>
                              {/* Due date */}
                              {inv.due_date && (
                                <span className="text-muted-foreground">due {inv.due_date}</span>
                              )}
                              {/* Status badge */}
                              <Badge className={cn('text-[10px] px-1.5 py-0', cfg.badge || '')}>
                                {cfg.label || deriveInvoiceStatus(inv)}
                              </Badge>
                              {hasPdf && (
                                <a href={inv.file_url} target="_blank" rel="noopener noreferrer" title="View PDF" className="inline-flex" onClick={(e) => e.stopPropagation()}>
                                  <Eye className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                                </a>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-mono py-1.5 text-green-600 dark:text-green-400">
                            {formatCurrency(cxInv(inv.amount_paid, inv.currency), dc)}
                          </TableCell>
                          <TableCell className="text-right font-mono py-1.5 text-amber-600 dark:text-amber-400">
                            {formatCurrency(cxInv(inv.total_amount, inv.currency) - cxInv(inv.amount_paid, inv.currency), dc)}
                          </TableCell>
                          <TableCell></TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      );
                    })}
                  </React.Fragment>
                  );
                })}
                {supplierGroups.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No supplier bills found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            )}
            {viewMode === 'by-month' && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Month</TableHead>
                  <TableHead className="text-right">Bills</TableHead>
                  <TableHead className="text-right">Invoiced</TableHead>
                  <TableHead className="text-right">Paid</TableHead>
                  <TableHead className="text-right">Outstanding</TableHead>
                  <TableHead className="text-right">Overdue</TableHead>
                  <TableHead className="text-right">Overdue Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {monthlyGroups.map((mg) => {
                  const isExpanded = expandedMonths.has(mg.monthKey);
                  const outstanding = mg.invoiced - mg.paid;
                  return (
                    <React.Fragment key={mg.monthKey}>
                      <TableRow
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => toggleMonth(mg.monthKey)}
                      >
                        <TableCell className="w-8 px-2">
                          {isExpanded
                            ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
                            : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                        </TableCell>
                        <TableCell className="font-medium text-sm">
                          {monthKeyToLongLabel(mg.monthKey)}
                        </TableCell>
                        <TableCell className="text-right text-sm text-muted-foreground">
                          {mg.invoices.length}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {formatCurrency(mg.invoiced, dc)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm text-green-600 dark:text-green-400">
                          {formatCurrency(mg.paid, dc)}
                        </TableCell>
                        <TableCell className={cn(
                          'text-right font-mono text-sm',
                          outstanding > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-foreground'
                        )}>
                          {formatCurrency(outstanding, dc)}
                        </TableCell>
                        <TableCell className="text-right text-sm">
                          {mg.overdueCount > 0 ? (
                            <Badge className="text-[10px] px-1.5 py-0 bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 border-0">
                              {mg.overdueCount}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">{'\u2014'}</span>
                          )}
                        </TableCell>
                        <TableCell className={cn(
                          'text-right font-mono text-sm',
                          mg.overdueAmount > 0 ? 'text-red-600 dark:text-red-400' : 'text-foreground'
                        )}>
                          {mg.overdueAmount > 0 ? formatCurrency(mg.overdueAmount, dc) : '\u2014'}
                        </TableCell>
                      </TableRow>
                      {isExpanded && mg.invoices.map((inv, i) => {
                        const cfg = STATUS_CONFIG[deriveInvoiceStatus(inv)] || {};
                        return (
                          <TableRow key={inv.bros_id || `${mg.monthKey}-${i}`} className="bg-muted/20 text-xs">
                            <TableCell></TableCell>
                            <TableCell colSpan={2}>
                              <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
                                <span className="font-medium">{inv.supplier_name || inv.company || '(unknown)'}</span>
                                {inv.property_name && (
                                  <span className="text-muted-foreground">{inv.property_name}</span>
                                )}
                                {inv.booking_id && (
                                  <a
                                    href={`https://internal.bonjour-residences.co.uk/bookings/${inv.booking_id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="font-mono text-blue-600 dark:text-blue-400 hover:underline"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {inv.booking_id}
                                  </a>
                                )}
                                {inv.invoice_number && (
                                  <span className="font-mono text-muted-foreground">{inv.invoice_number}</span>
                                )}
                                {inv.due_date && (
                                  <span className="text-muted-foreground">due {inv.due_date}</span>
                                )}
                                <Badge className={cn('text-[10px] px-1.5 py-0', cfg.badge || '')}>
                                  {cfg.label || deriveInvoiceStatus(inv)}
                                </Badge>
                              </div>
                            </TableCell>
                            <TableCell className="text-right font-mono text-xs">
                              <span className="inline-flex items-center gap-1">
                                {inv.currency && inv.currency !== dc && (
                                  <Badge variant="outline" className="text-[10px] px-1 py-0">{inv.currency}</Badge>
                                )}
                                {formatCurrency(cxInv(inv.total_amount, inv.currency), dc)}
                              </span>
                            </TableCell>
                            <TableCell className="text-right font-mono text-xs text-green-600 dark:text-green-400">
                              {formatCurrency(cxInv(inv.amount_paid, inv.currency), dc)}
                            </TableCell>
                            <TableCell className="text-right font-mono text-xs text-amber-600 dark:text-amber-400">
                              {formatCurrency(cxInv(inv.total_amount, inv.currency) - cxInv(inv.amount_paid, inv.currency), dc)}
                            </TableCell>
                            <TableCell></TableCell>
                            <TableCell></TableCell>
                          </TableRow>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
                {monthlyGroups.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No supplier bills found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// \u2500\u2500 Main Page \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500

export function BROSCostsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') ?? 'overview';

  const [period, setPeriod] = useState('ytd');
  const { displayCurrency } = usePortfolioPrefsStore();
  const dc = displayCurrency;

  const [dashData, setDashData] = useState(null);
  const [dashLoading, setDashLoading] = useState(false);

  // Currency conversion
  const gbpToEur = useMemo(() => {
    const eurGbp = dashData?.fx_rates?.EUR_GBP;
    return eurGbp && eurGbp > 0 ? 1 / eurGbp : null;
  }, [dashData]);

  const cx = useCallback(
    (v) => (dc === 'EUR' && gbpToEur) ? v * gbpToEur : v,
    [dc, gbpToEur]
  );

  const [supplierInvoices, setSupplierInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);

  const fetchDash = useCallback(async (p) => {
    setDashLoading(true);
    try {
      const res = await reportsAPI.brosDashboard(p);
      setDashData(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDashLoading(false);
    }
  }, []);

  const fetchInvoices = useCallback(async () => {
    setInvoicesLoading(true);
    try {
      const res = await brosAPI.getInvoices({ type: 'supplier', limit: 5000 });
      setSupplierInvoices(res.data.invoices ?? []);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setInvoicesLoading(false);
    }
  }, []);

  // Invoice financing state (declared before handleRefresh which references fetchFinancingData)
  const [financingData, setFinancingData] = useState(null);
  const [financingLoading, setFinancingLoading] = useState(false);
  const [financingError, setFinancingError] = useState(false);
  const [financingConfig, setFinancingConfig] = useState(null);
  const [financingConfigSaving, setFinancingConfigSaving] = useState(false);
  const [financingSortField, setFinancingSortField] = useState('interest');
  const [financingSortAsc, setFinancingSortAsc] = useState(false);
  const [financingOverrides, setFinancingOverrides] = useState({});

  const fetchFinancingOverrides = useCallback(async () => {
    try {
      const { data } = await invoiceFinancingAPI.getOverrides();
      const map = {};
      for (const o of (data.overrides ?? [])) {
        map[o.invoice_id] = o;
      }
      setFinancingOverrides(map);
    } catch (_) {
      // Non-critical
    }
  }, []);

  const fetchFinancingData = useCallback(async () => {
    setFinancingLoading(true);
    setFinancingError(false);
    try {
      const now = new Date();
      const currentYear = now.getFullYear();
      const asOf = `${currentYear}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      const { data: result } = await invoiceFinancingAPI.getData({ as_of: asOf });
      setFinancingData(result);
    } catch (_) {
      setFinancingError(true);
      setFinancingData(null);
    } finally {
      setFinancingLoading(false);
    }
  }, []);

  const fetchFinancingConfig = useCallback(async () => {
    try {
      const { data: cfg } = await invoiceFinancingAPI.getConfig();
      setFinancingConfig(cfg);
    } catch (_) {
      // Non-critical
    }
  }, []);

  const toggleFinancingExclusion = useCallback(async (invoiceId, invoiceNumber, client, reason) => {
    const existing = financingOverrides[invoiceId];
    try {
      if (existing) {
        await invoiceFinancingAPI.deleteOverride(existing._id);
        toast.success('Invoice re-included in financing');
      } else {
        await invoiceFinancingAPI.createOverride({
          invoice_id: invoiceId,
          invoice_number: invoiceNumber ?? '',
          client: client ?? '',
          reason,
        });
        toast.success('Invoice excluded from financing');
      }
      await fetchFinancingOverrides();
      await fetchFinancingData();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }, [financingOverrides, fetchFinancingOverrides, fetchFinancingData]);

  const saveFinancingConfig = useCallback(async (updates) => {
    setFinancingConfigSaving(true);
    try {
      const { data: cfg } = await invoiceFinancingAPI.updateConfig(updates);
      setFinancingConfig(cfg);
      toast.success('Financing config updated');
      await fetchFinancingData();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setFinancingConfigSaving(false);
    }
  }, [fetchFinancingData]);

  useEffect(() => {
    fetchDash(period);
    fetchInvoices();
  }, [period, fetchDash, fetchInvoices]);

  const handleRefresh = useCallback(() => {
    fetchDash(period);
    fetchInvoices();
    fetchFinancingData();
  }, [period, fetchDash, fetchInvoices, fetchFinancingData]);

  const handleTabChange = useCallback((tab) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      return next;
    });
  }, [setSearchParams]);

  const isLoading = dashLoading || invoicesLoading;

  useEffect(() => {
    fetchFinancingData();
    fetchFinancingOverrides();
  }, [fetchFinancingData, fetchFinancingOverrides]);

  useEffect(() => {
    fetchFinancingConfig();
  }, [fetchFinancingConfig]);

  // Export state
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [isExportingExcel, setIsExportingExcel] = useState(false);
  const billsExportScopeRef = React.useRef({ viewMode: 'by-supplier', expandedSuppliers: new Set(), supplierGroups: [] });

  // Precomputed chart data for exports (shared with OverviewTab)
  const revCostsChartData = useMemo(
    () => buildSupplierOverviewChartData(dashData?.months, supplierInvoices, cx),
    [dashData, supplierInvoices, cx]
  );
  const billsChartData = useMemo(
    () => buildSupplierBillsChartData(supplierInvoices, cx),
    [supplierInvoices, cx]
  );

  // Bookings-costs monthly rows for export
  const bookingsCostsRows = useMemo(() => {
    if (!dashData?.months) return [];
    return dashData.months
      .slice()
      .sort((a, b) => (a.month ?? '').localeCompare(b.month ?? ''))
      .map((m) => {
        const rev = Number(m.turnover ?? 0);
        const cogs = Number(m.cogs ?? 0);
        const margin = Number(m.gross_margin ?? 0);
        return {
          monthKey: m.month,
          monthLabel: monthKeyToLongLabel(m.month),
          monthShort: monthKeyToShortLabel(m.month),
          revenue: cx(rev),
          cogs: cx(cogs),
          margin: cx(margin),
          marginPct: rev > 0 ? (margin / rev) * 100 : null,
          bookingCount: m.booking_count ?? 0,
        };
      });
  }, [dashData, cx]);

  // Currency conversion for exports.
  // Pass full fx_rates map so DKK, USD, etc. convert correctly.
  const exportCxInv = useCallback((amount, invCurrency) => {
    if (amount == null) return 0;
    return convertAmount(amount, invCurrency || 'GBP', dc, dashData?.fx_rates) || 0;
  }, [dc, dashData]);

  // Resolve export invoices: scoped to expanded supplier in by-supplier view, full set otherwise
  // Scoped export: in by-supplier view with exactly one supplier expanded,
  // export only that supplier's bills. Otherwise fall back to full set.
  const getScopedBillsExportInvoices = useCallback(() => {
    const scope = billsExportScopeRef.current;
    if (scope.viewMode === 'by-supplier' && scope.expandedSuppliers.size === 1) {
      const supplierName = Array.from(scope.expandedSuppliers)[0];
      const group = scope.supplierGroups.find((g) => g.supplier === supplierName);
      if (group) return group.invoices;
    }
    return supplierInvoices;
  }, [supplierInvoices]);

  const handleExportExcel = useCallback(async () => {
    setIsExportingExcel(true);
    try {
      await exportCostsExcel({
        activeTab,
        dc,
        revCostsChartData,
        billsChartData,
        monthlyRows: bookingsCostsRows,
        invoices: getScopedBillsExportInvoices(),
        cxInv: exportCxInv,
      });
      toast.success('Excel exported');
    } catch (err) {
      console.error('Excel export failed:', err);
      toast.error(getErrorMessage(err));
    } finally {
      setIsExportingExcel(false);
    }
  }, [activeTab, dc, revCostsChartData, billsChartData, bookingsCostsRows, exportCxInv, getScopedBillsExportInvoices]);

  const handleExportPDF = useCallback(async () => {
    setIsExportingPDF(true);
    try {
      await exportCostsPDF({
        activeTab,
        dc,
        revCostsChartData,
        billsChartData,
        monthlyRows: bookingsCostsRows,
        invoices: getScopedBillsExportInvoices(),
        cxInv: exportCxInv,
      });
      toast.success('PDF exported');
    } catch (err) {
      console.error('PDF export failed:', err);
      toast.error(getErrorMessage(err));
    } finally {
      setIsExportingPDF(false);
    }
  }, [activeTab, dc, revCostsChartData, billsChartData, bookingsCostsRows, exportCxInv, getScopedBillsExportInvoices]);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Costs</h1>
            <p className="text-muted-foreground mt-1">
              BR OS supplier costs: accommodation costs, supplier invoicing, and payments {'\u00B7'} {dc}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <PeriodPills period={period} onPeriodChange={setPeriod} />
            <CurrencyToggle />
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportPDF}
              disabled={isExportingPDF}
              data-testid="export-pdf-btn"
            >
              {isExportingPDF
                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                : <Download className="h-4 w-4 mr-2" />}
              Export PDF
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportExcel}
              disabled={isExportingExcel}
              data-testid="export-excel-btn"
            >
              {isExportingExcel
                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                : <Download className="h-4 w-4 mr-2" />}
              Export Excel
            </Button>
            <Button variant="outline" size="icon" onClick={handleRefresh} disabled={isLoading}>
              <RefreshCw className={cn('h-4 w-4', isLoading && 'animate-spin')} />
            </Button>
          </div>
        </div>

        {/* Tab bar (underline style) */}
        <div className="border-b">
          <nav className="flex gap-0" aria-label="Costs tabs">
            {TABS.map((tab) => (
              <button
                key={tab.value}
                onClick={() => handleTabChange(tab.value)}
                className={cn(
                  'px-4 py-2.5 text-sm font-medium border-b-2 transition-colors',
                  activeTab === tab.value
                    ? 'border-foreground text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
                )}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab content */}
        {activeTab === 'overview' && (
          <OverviewTab
            period={period}
            dc={dc}
            cx={cx}
            dashData={dashData}
            dashLoading={dashLoading}
            supplierInvoices={supplierInvoices}
            invoicesLoading={invoicesLoading}
            cxInv={exportCxInv}
          />
        )}

        {activeTab === 'bookings-costs' && (
          <BookingsCostsTab
            period={period}
            dc={dc}
            cx={cx}
            dashData={dashData}
            dashLoading={dashLoading}
          />
        )}

        {activeTab === 'bills-payments' && (
          <BillsPaymentsTab
            dc={dc}
            fxRates={dashData?.fx_rates}
            invoices={supplierInvoices}
            loading={invoicesLoading}
            onRefresh={fetchInvoices}
            exportScopeRef={billsExportScopeRef}
          />
        )}

        {activeTab === 'invoice-financing' && (
          <InvoiceFinancingTab
            dc={dc}
            financingData={financingData}
            financingLoading={financingLoading}
            financingError={financingError}
            financingConfig={financingConfig}
            financingConfigSaving={financingConfigSaving}
            financingSortField={financingSortField}
            financingSortAsc={financingSortAsc}
            onSortFieldChange={setFinancingSortField}
            onSortAscChange={setFinancingSortAsc}
            onSaveConfig={saveFinancingConfig}
            overrides={financingOverrides}
            onToggleExclusion={toggleFinancingExclusion}
          />
        )}
      </div>
    </DashboardLayout>
  );
}


// ── Invoice Financing tab ─────────────────────────────────────────────

function InvoiceFinancingTab(props) {
  const {
    dc, financingData, financingLoading, financingError,
    financingConfig, financingConfigSaving,
    financingSortField, financingSortAsc,
    onSortFieldChange, onSortAscChange, onSaveConfig,
    overrides, onToggleExclusion,
  } = props;

  const [excludeReason, setExcludeReason] = useState('');
  const [excludeTarget, setExcludeTarget] = useState(null);

  if (financingLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (financingError || (!financingLoading && financingData == null)) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center justify-center gap-2 text-amber-600">
            <AlertTriangle className="h-4 w-4" />
            <p className="text-sm font-medium">Financing data unavailable</p>
          </div>
          <p className="text-center text-xs text-muted-foreground mt-1">
            The invoice financing endpoint did not return data. Financing costs are excluded from any totals on this page.
          </p>
        </CardContent>
      </Card>
    );
  }

  const summary = financingData.summary;

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Interest This Month', key: 'interest_this_month' },
          { label: 'Interest YTD', key: 'interest_ytd' },
          { label: 'Fees YTD', key: 'fees_ytd' },
          { label: 'Total Financing Cost YTD', key: 'total_financing_cost_ytd', bold: true },
          { label: 'Outstanding Balance', key: 'outstanding_financed_balance' },
        ].map((card) => {
          const raw = summary?.[card.key];
          const parsed = raw != null ? Number(raw) : null;
          return (
            <Card key={card.label}>
              <CardContent className="pt-3 pb-2">
                <p className="text-[10px] font-medium text-muted-foreground mb-0.5">{card.label}</p>
                <p className={`text-sm tabular-nums font-mono ${card.bold ? 'font-bold' : 'font-medium'}`}>
                  {parsed != null && !isNaN(parsed)
                    ? formatCurrency(parsed, dc)
                    : <span className="text-muted-foreground">--</span>}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {(summary?.excluded_count ?? 0) > 0 && (
        <div className="flex items-center gap-2" data-testid="financing-excluded-count">
          <Badge variant="secondary" className="bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-400 text-xs">
            {summary.excluded_count} invoice{summary.excluded_count !== 1 ? 's' : ''} excluded from totals
          </Badge>
        </div>
      )}

      {/* Monthly series table */}
      {financingData.monthly_series?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Monthly Financing Cost</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Month</TableHead>
                    <TableHead className="text-xs text-right">Interest</TableHead>
                    <TableHead className="text-xs text-right">Fee</TableHead>
                    <TableHead className="text-xs text-right font-semibold">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {financingData.monthly_series.map((m) => {
                    const mn = parseInt(m.month.split('-')[1], 10);
                    return (
                      <TableRow key={m.month}>
                        <TableCell className="text-xs py-1.5">{MONTHS_SHORT[mn - 1]} {m.month.split('-')[0]}</TableCell>
                        <TableCell className="text-xs text-right py-1.5 font-mono tabular-nums">
                          {formatCurrency(Number(m.interest ?? 0), dc)}
                        </TableCell>
                        <TableCell className="text-xs text-right py-1.5 font-mono tabular-nums">
                          {formatCurrency(Number(m.fee ?? 0), dc)}
                        </TableCell>
                        <TableCell className="text-xs text-right py-1.5 font-mono tabular-nums font-semibold">
                          {formatCurrency(Number(m.total ?? 0), dc)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Per-invoice detail table */}
      {financingData.invoice_rows?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">
              Per-Invoice Detail ({financingData.invoice_rows.length} invoice{financingData.invoice_rows.length !== 1 ? 's' : ''})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Invoice</TableHead>
                    <TableHead className="text-xs">Client</TableHead>
                    <TableHead className="text-xs text-right">Gross</TableHead>
                    <TableHead className="text-xs text-right">Financed</TableHead>
                    <TableHead className="text-xs">Window</TableHead>
                    <TableHead
                      className="text-xs text-right cursor-pointer select-none"
                      onClick={() => {
                        if (financingSortField === 'days') onSortAscChange(!financingSortAsc);
                        else { onSortFieldChange('days'); onSortAscChange(false); }
                      }}
                    >
                      Days {financingSortField === 'days' ? (financingSortAsc ? '\u25B2' : '\u25BC') : ''}
                    </TableHead>
                    <TableHead
                      className="text-xs text-right cursor-pointer select-none"
                      onClick={() => {
                        if (financingSortField === 'interest') onSortAscChange(!financingSortAsc);
                        else { onSortFieldChange('interest'); onSortAscChange(false); }
                      }}
                    >
                      Interest {financingSortField === 'interest' ? (financingSortAsc ? '\u25B2' : '\u25BC') : ''}
                    </TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                    <TableHead className="text-xs text-center">Exclude</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[...financingData.invoice_rows]
                    .sort((a, b) => {
                      const av = financingSortField === 'days' ? a.days : Number(a.interest);
                      const bv = financingSortField === 'days' ? b.days : Number(b.interest);
                      return financingSortAsc ? av - bv : bv - av;
                    })
                    .map((row) => {
                      const isExcluded = !!row.excluded;
                      const override = overrides?.[row.invoice_id];
                      const rowCls = isExcluded ? 'opacity-50' : '';
                      return (
                        <TableRow key={row.invoice_id} className={rowCls} data-testid={isExcluded ? 'financing-excluded-row' : undefined}>
                          <TableCell className="text-xs py-1.5 font-mono">{row.invoice_number ?? row.invoice_id}</TableCell>
                          <TableCell className="text-xs py-1.5">{row.client}</TableCell>
                          <TableCell className="text-xs text-right py-1.5 font-mono tabular-nums">
                            {formatCurrency(Number(row.gross_amount ?? 0), dc)}
                          </TableCell>
                          <TableCell className="text-xs text-right py-1.5 font-mono tabular-nums">
                            {formatCurrency(Number(row.financed_base ?? 0), dc)}
                          </TableCell>
                          <TableCell className="text-xs py-1.5 text-muted-foreground">
                            {row.window_from} to {row.window_to}
                          </TableCell>
                          <TableCell className="text-xs text-right py-1.5 font-mono tabular-nums">{row.days}</TableCell>
                          <TableCell className="text-xs text-right py-1.5 font-mono tabular-nums font-semibold">
                            {formatCurrency(Number(row.interest ?? 0), dc)}
                          </TableCell>
                          <TableCell className="text-xs py-1.5">
                            <Badge
                              variant="secondary"
                              className={row.is_paid
                                ? 'bg-green-100 text-green-700 dark:bg-green-950/40 dark:text-green-400 text-[10px]'
                                : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-950/40 dark:text-yellow-400 text-[10px]'
                              }
                            >
                              {row.is_paid ? 'Paid' : 'Unpaid'}
                            </Badge>
                            {isExcluded && (
                              <Badge variant="secondary" className="ml-1 bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400 text-[10px]" title={override?.reason ?? 'Excluded'}>
                                Excluded
                              </Badge>
                            )}
                            {row.clamped && (
                              <Badge variant="secondary" className="ml-1 bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-400 text-[10px]">
                                Clamped
                              </Badge>
                            )}
                            {row.is_paid && row.accrual_end_source === 'as_of' && (
                              <Badge variant="secondary" className="ml-1 bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400 text-[10px]" title="Paid but no due_date; accrual end uses reference date">
                                No Due Date
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-xs py-1.5 text-center">
                            {isExcluded ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 text-[10px] px-2"
                                data-testid="financing-include-btn"
                                onClick={() => onToggleExclusion(row.invoice_id)}
                              >
                                Include
                              </Button>
                            ) : excludeTarget === row.invoice_id ? (
                              <div className="flex items-center gap-1">
                                <input
                                  type="text"
                                  placeholder="Reason (required)"
                                  value={excludeReason}
                                  onChange={(e) => setExcludeReason(e.target.value)}
                                  className="h-6 w-28 rounded border border-input bg-background px-1 text-[10px]"
                                  data-testid="financing-exclude-reason"
                                  autoFocus
                                  onKeyDown={(e) => {
                                    if (e.key === 'Escape') { setExcludeTarget(null); setExcludeReason(''); }
                                    if (e.key === 'Enter' && excludeReason.trim()) {
                                      onToggleExclusion(row.invoice_id, row.invoice_number, row.client, excludeReason.trim());
                                      setExcludeTarget(null);
                                      setExcludeReason('');
                                    }
                                  }}
                                />
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 text-[10px] px-1.5"
                                  disabled={!excludeReason.trim()}
                                  onClick={() => {
                                    if (excludeReason.trim()) {
                                      onToggleExclusion(row.invoice_id, row.invoice_number, row.client, excludeReason.trim());
                                      setExcludeTarget(null);
                                      setExcludeReason('');
                                    }
                                  }}
                                >
                                  OK
                                </Button>
                              </div>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 text-[10px] px-2 text-muted-foreground"
                                data-testid="financing-exclude-btn"
                                onClick={() => { setExcludeTarget(row.invoice_id); setExcludeReason(''); }}
                              >
                                Exclude
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Facility configuration editor */}
      {financingConfig && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Facility Configuration</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">Annual Rate (%)</label>
                <input
                  type="text"
                  defaultValue={String(Number(financingConfig.annual_rate ?? '0.08') * 100)}
                  onBlur={(e) => {
                    const pct = Number(e.target.value);
                    if (!isNaN(pct) && pct > 0 && pct <= 100) {
                      onSaveConfig({ annual_rate: String(pct / 100) });
                    }
                  }}
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm font-mono"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">Monthly Fee (GBP)</label>
                <input
                  type="text"
                  defaultValue={financingConfig.monthly_fee ?? '400'}
                  onBlur={(e) => {
                    const val = e.target.value.trim();
                    if (val && !isNaN(Number(val))) {
                      onSaveConfig({ monthly_fee: val });
                    }
                  }}
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm font-mono"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">Advance Rate</label>
                <input
                  type="text"
                  defaultValue={financingConfig.advance_rate ?? '1.0'}
                  onBlur={(e) => {
                    const val = e.target.value.trim();
                    if (val && !isNaN(Number(val))) {
                      onSaveConfig({ advance_rate: val });
                    }
                  }}
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm font-mono"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">Facility Start Date</label>
                <input
                  type="date"
                  defaultValue={financingConfig.facility_start_date ?? ''}
                  onBlur={(e) => {
                    onSaveConfig({ facility_start_date: e.target.value || null });
                  }}
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm font-mono"
                />
              </div>
            </div>
            {financingConfigSaving && (
              <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" /> Saving...
              </p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
